/**
 * Handlers especializados para erros HTTP
 * Separa a lógica de tratamento de erros por status code
 * Responsabilidade única: converter status codes em ações
 */
import { AxiosError } from 'axios';
import type { TenantSource } from '@/lib/tenant';

export interface HttpErrorContext {
  error: AxiosError;
  requestUrl: string;
  tenant: string | null;
  /** De onde o tenant foi resolvido — decide se o redirect de login precisa de prefixo no path. */
  tenantSource: TenantSource;
}

/**
 * Handler para erro 403 (Forbidden)
 */
export function handleForbiddenError(context: HttpErrorContext): void {
  const { requestUrl } = context;
  if (requestUrl.includes('/api/appliance/')) {
    // Dispara evento que será capturado pelo AppError middleware
    window.dispatchEvent(
      new CustomEvent('http-error', {
        detail: { status: 403, message: 'Você não tem acesso a este appliance.' },
      })
    );
  }
}

/**
 * Handler para erro 401 (Unauthorized)
 * Retorna true se foi tratado, false caso contrário
 */
export function handleUnauthorizedError(context: HttpErrorContext): boolean {
  const { requestUrl, tenant, tenantSource } = context;

  // AuthWrapper já trata o 401 de /api/auth/me; ignorar aqui
  if (requestUrl.includes('/api/auth/me') || window.location.pathname.endsWith('/login')) {
    return false;
  }

  // Login por token de acesso único: a própria página trata o 401 do
  // /api/login/token (link inválido/já usado/expirado) e mostra o erro
  // inline. Redirecionar daqui por cima queimaria a UX do link mágico com
  // um "/login?r=..." que nem chega a bater com nenhuma rota (ver
  // performLoginRedirect) antes de a página conseguir reagir sozinha.
  if (window.location.pathname.endsWith('/login/token')) {
    return false;
  }

  // Portal cativo (/api/portal/*) é público: não há sessão a expirar — 401 ali
  // é "usuário ou senha inválidos" e quem exibe é a própria página do portal.
  // Redirecionar levaria o cliente do hotspot ao login administrativo.
  if (requestUrl.includes('/api/portal/')) {
    return false;
  }

  // Redirecionar para login
  performLoginRedirect(tenant, tenantSource);
  return true;
}

/**
 * Realiza o redirecionamento para login
 * Responsabilidade única: navegar o usuário
 */
function performLoginRedirect(tenant: string | null, tenantSource: TenantSource): void {
  // Sob subdomínio o tenant já está no host — prefixar o path com ele de
  // novo duplica o tenant (ex.: tenant.dev.local/tenant/login) e o
  // React Router, com basename vazio nesse modo, não bate com nenhuma rota:
  // 404. Só o formato legado (tenant no path) precisa do prefixo aqui.
  const base = tenantSource === 'path' && tenant ? `/${tenant}` : '';
  const fullPath = window.location.pathname + window.location.search;

  // Remove o prefixo do basename para obter o path React Router (evita duplicação)
  const routerPath = base
    ? fullPath.replace(new RegExp(`^${base}`), '') || '/'
    : fullPath;

  window.location.href = `${base}/login?r=${encodeURIComponent(routerPath)}`;
}
